import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  Paper,
  Table,
  TableBody,
  TableContainer,
  Link
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import FormRow from './FormRow';
import { getTimezoneLabel, formatDateTimeWithTimezone } from '../../utils/timezoneUtils';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../constants/roles';

const PRIMARY_LIVE_USAGE_KEYS = ['airframeHours', 'landings', 'engine1Hours', 'engine2Hours'];
const HOURS_USAGE_KEYS = new Set(['airframeHours', 'engine1Hours', 'engine2Hours']);

const humanizeUsageKey = (key) =>
  String(key || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());

const getLiveUsageLabel = (key) => {
  const map = {
    airframeHours: 'Airframe Total Hours (Live)',
    landings: 'Total Landings (Live)',
    engine1Hours: 'Engine 1 Total Hours (Live)',
    engine2Hours: 'Engine 2 Total Hours (Live)'
  };
  return map[key] || `${humanizeUsageKey(key)} (Live)`;
};

const formatLiveUsageValue = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const asNumber = Number(value);
  if (!Number.isNaN(asNumber) && Number.isFinite(asNumber)) {
    return asNumber.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }
  return String(value);
};

const getHoursBreakdownLabel = (value) => {
  const asNumber = Number(value);
  if (Number.isNaN(asNumber) || !Number.isFinite(asNumber) || asNumber < 0) return '';
  const totalMinutes = Math.round(asNumber * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const hhmm = `${hours}:${String(minutes).padStart(2, '0')}`;
  return `HH:MM ${hhmm} (${hours} Hours, ${minutes} Minutes)`;
};

const formatAgeDisplay = (value) => {
  if (value === null || value === undefined) return '—';
  return formatLiveUsageValue(value);
};

/**
 * Read-only live/current/total aircraft usage (same source as GET /api/aircraft/:id/live-usage).
 */
const LiveAircraftUsagePanel = ({
  liveUsage,
  liveUsageLoading,
  liveUsageError,
  displayTimezone,
  title = 'Live/Current/Total Aircraft Usage',
  infoAlert = null,
  onRefresh = null,
  paperSx = { mb: 3 }
}) => {
  const { user } = useAuth();
  const tzLabel = displayTimezone ? getTimezoneLabel(displayTimezone) : '';
  const currentUsageTotals = liveUsage?.currentUsageTotals || null;
  const usageValues =
    currentUsageTotals?.values && typeof currentUsageTotals.values === 'object'
      ? currentUsageTotals.values
      : {};
  const engineSerials = currentUsageTotals?.engineSerials || {};
  const usageKeys = Object.keys(usageValues);
  const orderedUsageKeys = [
    ...PRIMARY_LIVE_USAGE_KEYS.filter((key) => usageKeys.includes(key)),
    ...usageKeys.filter((key) => !PRIMARY_LIVE_USAGE_KEYS.includes(key)).sort((a, b) => a.localeCompare(b))
  ];
  const asOnDisplay = currentUsageTotals?.asOnDateTime
    ? formatDateTimeWithTimezone(currentUsageTotals.asOnDateTime, null, displayTimezone)
    : '—';

  const aircraftAgeLive = liveUsage?.aircraftAgeLive;
  const ageAsOnDisplay = aircraftAgeLive?.serverComputedAt
    ? formatDateTimeWithTimezone(aircraftAgeLive.serverComputedAt, null, displayTimezone)
    : '—';

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        ...paperSx
      }}
    >
      <Box
        sx={{
          p: 1.5,
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {onRefresh && (
          <Button
            color="inherit"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            disabled={liveUsageLoading}
            sx={{ textTransform: 'none', flexShrink: 0 }}
          >
            Refresh
          </Button>
        )}
      </Box>
      <Box sx={{ p: 2.5 }}>
        {infoAlert ? <Box sx={{ mb: 2 }}>{infoAlert}</Box> : null}
        <Alert severity="info" sx={{ mb: 2, borderRadius: 1 }}>
          Values come from the latest <strong>approved</strong> Technical Log Book entry for this aircraft (carried-forward
          totals only).
          {user?.role === ROLES.SUPER_ADMIN ? (
            <>
              {' '}
              Field names used for live assembly/component math are configured under{' '}
              <Link component={RouterLink} to="/client-settings" underline="hover">
                Client Settings → Live TLB mapping
              </Link>
              .
            </>
          ) : null}
        </Alert>
        {liveUsageLoading ? (
          <Alert severity="info" sx={{ borderRadius: 1 }}>
            Loading live usage...
          </Alert>
        ) : liveUsageError ? (
          <Alert severity="warning" sx={{ borderRadius: 1 }}>
            {liveUsageError}
          </Alert>
        ) : (
          <>
            {aircraftAgeLive ? (
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableBody>
                    <FormRow label="Aircraft Days (Live)">
                      <TextField
                        fullWidth
                        size="small"
                        value={formatAgeDisplay(aircraftAgeLive.aircraftDaysLive)}
                        InputProps={{ readOnly: true }}
                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'action.hover' } }}
                      />
                    </FormRow>
                    <FormRow label="Aircraft Years (Live)">
                      <TextField
                        fullWidth
                        size="small"
                        value={formatAgeDisplay(aircraftAgeLive.aircraftYearsLive)}
                        InputProps={{ readOnly: true }}
                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'action.hover' } }}
                      />
                    </FormRow>
                    <FormRow label={tzLabel ? `Age as of server date (${tzLabel})` : 'Age as of server date'}>
                      <TextField
                        fullWidth
                        size="small"
                        value={ageAsOnDisplay}
                        InputProps={{ readOnly: true }}
                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'action.hover' } }}
                      />
                    </FormRow>
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}
            {orderedUsageKeys.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 1 }}>
                No live usage available yet.
              </Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {orderedUsageKeys.map((key) => (
                      <React.Fragment key={key}>
                        <FormRow label={getLiveUsageLabel(key)}>
                          <Box>
                            <TextField
                              fullWidth
                              size="small"
                              value={formatLiveUsageValue(usageValues[key])}
                              InputProps={{ readOnly: true }}
                              sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'action.hover' } }}
                            />
                            {HOURS_USAGE_KEYS.has(key) && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {getHoursBreakdownLabel(usageValues[key])}
                              </Typography>
                            )}
                          </Box>
                        </FormRow>
                        {key === 'engine1Hours' && (
                          <FormRow label="Engine 1 Serial (Live Evidence)">
                            <TextField
                              fullWidth
                              size="small"
                              value={engineSerials.engine1Serial || '—'}
                              InputProps={{ readOnly: true }}
                              sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'action.hover' } }}
                            />
                          </FormRow>
                        )}
                        {key === 'engine2Hours' && (
                          <FormRow label="Engine 2 Serial (Live Evidence)">
                            <TextField
                              fullWidth
                              size="small"
                              value={engineSerials.engine2Serial || '—'}
                              InputProps={{ readOnly: true }}
                              sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'action.hover' } }}
                            />
                          </FormRow>
                        )}
                      </React.Fragment>
                    ))}
                    <FormRow label={tzLabel ? `As on Date/Time (${tzLabel})` : 'As on Date/Time'}>
                      <TextField
                        fullWidth
                        size="small"
                        value={asOnDisplay}
                        InputProps={{ readOnly: true }}
                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'action.hover' } }}
                      />
                    </FormRow>
                    <FormRow label="Source / Evidence">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <TextField
                          size="small"
                          value={currentUsageTotals?.sourceLabel || '—'}
                          InputProps={{ readOnly: true }}
                          sx={{ minWidth: 260, '& .MuiOutlinedInput-root': { bgcolor: 'action.hover' } }}
                        />
                        {currentUsageTotals?.evidenceUrl && currentUsageTotals?.sourceId && (
                          <Link
                            component={RouterLink}
                            to={currentUsageTotals.evidenceUrl}
                            state={currentUsageTotals.evidenceState || { openEntryId: currentUsageTotals.sourceId }}
                            underline="hover"
                            sx={{ fontWeight: 500 }}
                          >
                            View Evidence
                          </Link>
                        )}
                      </Box>
                    </FormRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};

export default LiveAircraftUsagePanel;
